/** @jsxImportSource @emotion/react */
import React, { Fragment } from 'react';
import { StylesClassName, UseStyles } from 'types/styles';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Heading from 'components/Heading';
import { FormattedMessage, useIntl } from 'react-intl';
import { Has } from 'components/Has';
import { Permission } from 'types/dto';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { paths } from 'router';
import { Link } from 'react-router-dom';
import { useNationalPlanningProcess } from 'pages/UploadsPage/useNationalPlanningProcess';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import setSpacing from 'utils/spacing';
import TabGroupIcon from 'components/icons/TabGroupIcon';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { config } from 'application';
import * as ga from 'application/ga';

interface NationalPlanningProcessProps {
  className?: StylesClassName;
}
const fakeCategoryIds = ['inpp'];

const NationalPlanningProcess: React.FC<NationalPlanningProcessProps> = ({ className }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { formatDate, formatMessage } = useIntl();
  const {
    countries,
    isCountriesLoading,
    currentCountryId,
    setCurrentCountryId,
    planProgressList,
    isPlanProgressListLoading,
    planResultsList,
    isPlanResultsListLoading,
    createHandleDownload,
    createHandleDownloadResult,
  } = useNationalPlanningProcess();
  const isCountriesEmpty = !isCountriesLoading && countries?.length === 0;

  return (
    <Box p={8} css={[classes.NationalPlanningProcess, className]}>
      <Box css={classes.header} mb={6}>
        <Box display="flex" alignItems="center" columnGap={2} flexGrow={1}>
          <Heading component="h2" mb={0}>
            <FormattedMessage id={'UploadsPage.nationalPlanningProcess'} defaultMessage={'National planning process'} />
          </Heading>
          <Select
            fullWidth
            css={classes.countries}
            placeholder="Test"
            disabled={isCountriesLoading || isCountriesEmpty}
            value={currentCountryId ?? -1}
            onChange={e => setCurrentCountryId(Number(e.target.value))}
          >
            <MenuItem value={-1} className="SelectPlaceholder" disabled>
              {isCountriesLoading ? formatMessage({ id: 'common.loading', defaultMessage: 'Loading...' }) : ''}
            </MenuItem>
            {countries?.map(country => (
              <MenuItem key={country.id} value={country.id}>
                {country.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Has any={[Permission.GeneralAll, Permission.PlanEdit]} scope={{ skip: true }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ whiteSpace: 'nowrap' }}
            endIcon={<FileUploadOutlinedIcon />}
            component={Link}
            to={paths.upload.generate()}
          >
            <FormattedMessage id={'UploadsPage.uploadNewVersion'} defaultMessage={'Upload new version'} />
          </Button>
        </Has>
      </Box>
      {isPlanProgressListLoading && <CircularProgress />}
      {!isPlanProgressListLoading && (
        <Box css={classes.categories}>
          {Object.entries(planProgressList)
            .filter(([categoryCode]) => !fakeCategoryIds.includes(categoryCode))
            .map(([categoryCode, category], index) => (
              <Box css={classes.category} key={categoryCode}>
                {category.templates.map(({ template, plan }) => (
                  <Box css={classes.template} key={template.id}>
                    <div
                      css={classes.num}
                      style={{ color: category?.color, background: category?.bg }}
                      className="CategoryTemplateNum"
                    >
                      {index + 1}
                    </div>
                    <div css={classes.main}>
                      <div css={classes.categoryCode}>{category.name}</div>
                      <Typography component="div" variant="body1" css={classes.templateName}>
                        {template.name}
                      </Typography>
                      {!plan && (
                        <Typography component="div" variant="body1" css={classes.templateName}>
                          <span css={classes.emptyName}>Template not yet uploaded</span>
                        </Typography>
                      )}
                    </div>
                    {plan && (
                      <>
                        <Typography component="div" variant="body1" css={classes.area}>
                          {plan.adminArea?.name || (
                            <FormattedMessage id={'common.national'} defaultMessage={'National'} />
                          )}
                        </Typography>
                        <Typography component="div" variant="body1" css={classes.date}>
                          {formatDate(plan.createdDate, config.dateFormat)}
                        </Typography>
                      </>
                    )}
                    <div css={classes.actions}>
                      {plan ? (
                        <>
                          {(plan.access.edit || plan.access.download) && (
                            <>
                              <Tooltip
                                title={
                                  <Box display="flex" alignItems="center" gap={2} sx={{ wordBreak: 'break-word' }}>
                                    <ArticleOutlinedIcon color="primary" />
                                    {plan.file.name}
                                  </Box>
                                }
                                placement="top-end"
                              >
                                <InfoOutlinedIcon color="primary" sx={{ display: 'block' }} />
                              </Tooltip>
                              <IconButton css={classes.downloadBtn} onClick={createHandleDownload(plan.id)}>
                                <FileDownloadOutlinedIcon />
                              </IconButton>
                            </>
                          )}
                        </>
                      ) : (
                        !template.isComingLater && (
                          <a
                            href={`${config.nip.apiUrl}/plans/types/${template.id}/template`}
                            onClick={() => ga.downloadTemplate(template)}
                            download
                          >
                            <Button>Download template</Button>
                          </a>
                        )
                      )}
                    </div>
                  </Box>
                ))}
              </Box>
            ))}
        </Box>
      )}
      <Box css={classes.inpp}>
        <Box css={classes.categoryTitle} mb={4} p={2} pr={4} id="inpp">
          <span css={[classes.num, classes.autoCategoryNum]}>
            <TabGroupIcon />
          </span>
          <Typography component="h2" variant="subtitle1">
            Integrated national plans and proposals
          </Typography>
        </Box>
        {isPlanResultsListLoading && (
          <div>
            <CircularProgress />
          </div>
        )}
        {!isPlanResultsListLoading && (
          <Box css={classes.inppTemplates}>
            {planResultsList?.data.map(plan => (
              <Fragment key={plan.type?.id}>
                <Box css={classes.inppTemplate}>
                  <div css={classes.num} className="CategoryTemplateNum">
                    {plan.type?.code === 'NIP_RESULT' ? <AccountBalanceOutlinedIcon /> : <ArticleOutlinedIcon />}
                  </div>
                  <div css={classes.main}>
                    <Typography component="div" variant="body1" css={classes.planName}>
                      {plan.type?.name}
                    </Typography>
                  </div>
                  {plan.createdDate && (
                    <Typography component="div" variant="body1" css={classes.date}>
                      {formatDate(plan.createdDate, config.dateFormat)}
                    </Typography>
                  )}
                  <div css={classes.actions}>
                    {plan.file && (
                      <>
                        <Tooltip
                          title={
                            <Box display="flex" alignItems="center" gap={2} sx={{ wordBreak: 'break-word' }}>
                              <ArticleOutlinedIcon color="primary" />
                              {plan.file.name}
                            </Box>
                          }
                          placement="top-end"
                        >
                          <InfoOutlinedIcon color="primary" sx={{ display: 'block' }} />
                        </Tooltip>
                        <IconButton
                          css={classes.downloadBtn}
                          onClick={() => {
                            createHandleDownloadResult(plan.id);
                          }}
                        >
                          <FileDownloadOutlinedIcon />
                        </IconButton>
                      </>
                    )}
                  </div>
                </Box>
                <hr />
              </Fragment>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  NationalPlanningProcess: {},
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(6),
  },
  countries: {
    maxWidth: 200,
  },
  downloadBtn: {
    backgroundColor: theme.palette.custom.primary.brand[20],
    height: 44,
    width: 44,
    color: theme.palette.primary.main,
  },
  categories: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  category: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),

    '&:first-of-type .CategoryTemplateNum': {
      background: `${theme.palette.custom.primary.grey[20]} !important`,
    },
  },
  template: {
    padding: setSpacing([2, 6, 2, 4]),
    background: theme.palette.common.white,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),

    '&:not(:first-of-type)': {
      '& .CategoryTemplateNum': {
        opacity: 0,
        pointerEvents: 'none',
      },
    },
  },
  inppTemplate: {
    padding: setSpacing([2, 6, 2, 4]),
    background: theme.palette.common.white,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    minHeight: 64,
  },
  num: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,

    '& svg': {
      color: theme.palette.custom.secondary.purple[110],
    },
  },
  main: {
    flexGrow: 1,
  },
  categoryCode: {
    fontSize: theme.typography.custom.text.xs.fontSize,
    color: theme.palette.custom.primary.grey[150],
    fontWeight: 600,

    '&:first-of-type:last-of-type': {
      // fontSize:
    },
  },
  templateName: {
    color: theme.palette.custom.primary.brand[150],
    fontWeight: 600,
  },
  area: {},
  date: {
    color: theme.palette.custom.primary.grey[120],
    padding: setSpacing([0, 2]),
    whiteSpace: 'nowrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    flexShrink: 0,
  },
  emptyName: {
    color: theme.palette.custom.primary.grey[90],
  },
  categoryTitle: {
    display: 'inline-flex',
    alignItems: 'center',
    fontWeight: 500,
    height: 56,
    gap: theme.spacing(2),
    position: 'relative',
    background: theme.palette.custom.secondary.purple[120],
    borderRadius: 28,
    color: theme.palette.common.white,
  },
  inpp: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(4),
    borderTop: `1px solid ${theme.palette.custom.primary.grey[50]}`,
  },
  planName: {
    fontWeight: 600,
  },
  inppTemplates: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),

    hr: {
      margin: 0,
      border: 0,
      borderTop: `1px solid ${theme.palette.custom.primary.grey[50]}`,

      '&:last-of-type': {
        display: 'none',
      },
    },
  },
});

export default NationalPlanningProcess;
